/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 *
 * Portions derived from Grist (Copyright Grist Labs), used under the Apache
 * License, Version 2.0. See the NOTICE file for details.
 */

import { cssFocusedPanel } from "app/client/components/RegionFocusSwitcher";
import { transientInput } from "app/client/ui/transientInput";
import { bigBasicButton, primaryButton } from "app/client/ui2018/buttons";
import { mediaSmall, theme, vars } from "app/client/ui2018/cssVars";
import { icon } from "app/client/ui2018/icons";
import { components } from "app/common/ThemePrefs";

import { styled } from "grainjs";

// Import popweasel to ensure that sortSelector style below comes later in CSS than popweasel
// styles, which gives it priority.
import "popweasel";

export const docList = styled("div", `
  height: 100%;
  padding: 0px 40px 64px 40px;
  overflow-y: auto;
  position: relative;
  display: flex;
  flex-direction: column;

  @media ${mediaSmall} {
    & {
      padding: 0px 24px 64px 24px;
    }
  }
  @media print {
    & {
      display: none;
    }
  }
`);

export const docListContent = styled("div", `
  display: flex;
  width: 100%;
  max-width: 1340px;
  margin: 0 auto;
`);

export const docMenu = styled("div", `
  width: 100%;
`);

const headerWrap = styled("div", `
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`);

export const docListHeaderWrap = styled(headerWrap, `
  margin: 16px 0px 24px 0px;
`);

export const listHeader = styled("div", `
  min-height: 32px;
  line-height: 32px;
  color: ${theme.text};
  font-size: ${vars.xxxlargeFontSize};
  font-weight: ${vars.headerControlTextWeight};
`);

export const listHeaderNoWrap = styled(listHeader, `
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`);

export const docListHeader = styled(listHeader, `
  margin-bottom: 24px;
`);

export const workspaceHeaderWrap = styled("div", `
  display: flex;
  overflow: hidden;
`);

export const workspaceHeader = styled(listHeaderNoWrap, `
  font-size: 24px;
  font-weight: 500;
`);

export const templatesHeaderWrap = styled("div", `
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  margin: 16px 0px 24px 0px;

  @media ${mediaSmall} {
    & {
      flex-direction: column;
      align-items: flex-start;
    }
  }
`);

export const templatesHeader = styled(listHeader, `
  cursor: pointer;
`);

export const featuredTemplatesHeader = styled(docListHeader, `
  margin-top: 16px;
  display: flex;
  align-items: center;
`);

export const otherSitesHeader = templatesHeader;

export const STICKY_HEADER_HEIGHT_PX = 76;

// The main panel may have a focus ring, but it is hidden by the header that is sticky positioned
// at `top: 0px`. In theory we'd want to say that the sticky header is at `top: 3px` to compensate for
// potential focus ring; but in that case other issues arise (like we see 3px of content below the sticky
// header when scrolling).
// The trick here is to set a top border that is transparent by default, and colored when the main
// panel has focus. We basically replicate the top-side of the focus ring on the header.
export const stickyHeader = styled(headerWrap, `
  height: ${STICKY_HEADER_HEIGHT_PX}px;
  position: sticky;
  top: 0px;
  background-color: ${theme.mainPanelBg};
  z-index: ${vars.stickyHeaderZIndex};
  margin-bottom: 0px;
  padding: 13px 0px 24px 0px;
  border-top: 3px solid transparent;
  .${cssFocusedPanel.className}-focused:focus & {
    border-top-color: ${components.kbFocusHighlight};
  }
`);

export const allDocsTemplates = styled("div", `
  display: flex;
`);

export const docBlock = styled("div", `
  color: ${theme.text};
  max-width: 550px;
  min-width: 300px;
  margin-bottom: 28px;

  &-icons {
    max-width: max-content;
    min-width: calc(min(550px, 100%));
  }
`);

export const templatesDocBlock = styled(docBlock, `
  margin-top: 32px;
`);

export const otherSitesBlock = styled("div", `
  color: ${theme.text};
  margin-bottom: 32px;
`);

export const otherSitesButtons = styled("div", `
  display: flex;
  overflow: auto;
  padding-bottom: 16px;
  margin-top: 16px;
  margin-bottom: 28px;
  gap: 16px;
`);

export const siteButton = styled(bigBasicButton, `
  flex: 0 0 auto;
`);

export const docHeaderIcon = styled(icon, `
  margin-right: 8px;
  margin-top: -3px;
  --icon-color: ${theme.lightText};
`);

export const workspaceHeaderIcon = styled(icon, `
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  margin-right: 16px;
  --icon-color: ${theme.lightText};
`);

export const pinnedDocsIcon = styled(docHeaderIcon, `
  --icon-color: ${theme.text};
`);

export const featuredTemplatesIcon = styled(icon, `
  --icon-color: ${theme.text};
  margin-right: 8px;
  width: 20px;
  height: 20px;
`);

export const templatesHeaderIcon = styled(docHeaderIcon, `
  width: 24px;
  height: 24px;
`);

export const otherSitesHeaderIcon = templatesHeaderIcon;

const docBlockHeader = `
  display: flex;
  align-items: center;
  height: 40px;
  line-height: 40px;
  margin-bottom: 8px;
  margin-right: -16px;
  color: ${theme.text};
  font-size: ${vars.mediumFontSize};
  font-weight: bold;
  &, &:hover, &:focus {
    text-decoration: none;
    outline: none;
    color: inherit;
  }
`;

export const docBlockHeaderLink = styled("a", docBlockHeader);

export const templateBlockHeader = styled("div", docBlockHeader);

export const wsLeft = styled("div", `
  color: ${theme.text};
  flex: 1 0 50%;
  min-width: 0px;
  margin-right: 24px;
`);

export const docRowWrapper = styled("div", `
  position: relative;
  margin: 0px -16px 8px -16px;
  border-radius: 3px;
  font-size: ${vars.mediumFontSize};
  color: ${theme.text};
  --icon-color: ${theme.lightText};

  &:hover, &.weasel-popup-open {
    background-color: ${theme.hover};
  }
`);

export const docRowLink = styled("a", `
  display: flex;
  align-items: center;
  height: 40px;
  line-height: 40px;
  border-radius: 3px;
  outline: none;
  transition: background-color 2s;
  &, &:hover, &:focus {
    text-decoration: none;
    outline: none;
    color: inherit;
  }
  &-no-access, &-no-access:hover, &-no-access:focus {
    color: ${theme.disabledText};
    cursor: not-allowed;
  }
`);

export const docLeft = styled("div", `
  flex: 1 0 50%;
  min-width: 0px;
  margin: 0 16px;
  display: flex;
  align-items: center;
`);

export const docName = styled("div", `
  flex: 0 1 auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`);

export const docPinIcon = styled(icon, `
  flex: none;
  margin-left: 4px;
  --icon-color: ${theme.accentIcon};
`);

export const docPublicIcon = styled(icon, `
  flex: none;
  margin-left: auto;
  --icon-color: ${theme.accentIcon};
`);

export const docEditorInput = styled(transientInput, `
  flex: 1 0 50%;
  min-width: 0px;
  margin: 0 16px;
  color: initial;
  font-size: inherit;
  line-height: initial;
`);

export const docRowUpdatedAt = styled("div", `
  flex: 1 1 50%;
  color: ${theme.lightText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: normal;
`);

export const docMenuTrigger = styled("div", `
  flex: none;
  margin: 0 4px 0 auto;
  height: 24px;
  width: 24px;
  padding: 4px;
  line-height: 0px;
  border-radius: 3px;
  cursor: default;
  --icon-color: ${theme.docMenuDocOptionsFg};
  .${docRowLink.className}:hover > & {
    --icon-color: ${theme.docMenuDocOptionsHoverFg};
  }
  &:hover, &.weasel-popup-open {
    background-color: ${theme.docMenuDocOptionsHoverBg};
    --icon-color: ${theme.docMenuDocOptionsHoverFg};
  }
`);

export const moveDocModalBody = styled("div", `
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${theme.modalBorderDark};
  margin: 0 -64px;
  height: 200px;
`);

export const moveDocListItem = styled("div", `
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 32px;
  padding: 12px 64px;
  cursor: pointer;
  font-size: ${vars.mediumFontSize};

  &-selected {
    background-color: ${theme.moveDocsSelectedBg};
    color: ${theme.moveDocsSelectedFg};
  }
  &-disabled {
    color: ${theme.moveDocsDisabledFg};
    cursor: default;
  }
`);

export const moveDocListText = styled("div", `
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  justify-content: center;
`);

export const moveDocListHintText = styled(moveDocListText, `
  text-align: right;
`);

export const spinner = styled("div", `
  display: flex;
  align-items: center;
  height: 80px;
  margin: auto;
  margin-top: 80px;
`);

export const prefSelectors = styled("div", `
  float: right;
  display: flex;
  align-items: center;
`);

export const sortSelector = styled("div", `
  margin-right: 24px;

  /* negate the styles of a select that normally looks like a button */
  border: none;
  display: inline-flex;
  height: unset;
  line-height: unset;
  align-items: center;
  border-radius: ${vars.controlBorderRadius};
  color: ${theme.controlFg};
  --icon-color: ${theme.controlFg};
  background-color: unset;

  &:focus, &:hover {
    outline: none;
    box-shadow: none;
    background-color: ${theme.hover};
  }
  @media ${mediaSmall} {
    & {
      margin-right: 0;
    }
  }
`);

export const upgradeButton = styled("div", `
  margin-left: 32px;

  @media ${mediaSmall} {
    & {
      margin-left: 8px;
    }
  }
`);

export const upgradeCard = styled("div", `
  margin-left: 64px;
`);

export const paragraph = styled(docBlock, `
  color: ${theme.text};
  line-height: 1.6;
`);

export const introLine = styled(paragraph, `
  font-size: ${vars.introFontSize};
  margin-bottom: 8px;
`);

export const machineControlButton = styled(primaryButton, `
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.01em;
  padding: 6px 14px;
  border-radius: 9999px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 2px 6px rgba(0, 0, 0, 0.04);
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3), 0 4px 10px rgba(0, 0, 0, 0.08);
  }
  &:active {
    transform: scale(0.98);
  }

  @media ${mediaSmall} {
    & {
      padding: 5px 10px;
    }
  }
`);
